import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { useUserProfile } from "../contexts/UserProfileContext";
import CreateTripModal from "../components/CreateTripModal";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { Trip, TripJoinEntry, TripRequest } from "../types";

function useGlobalFlagInline(featureKey: string): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("status")
        .eq("feature_key", featureKey)
        .maybeSingle();
      if (!alive) return;
      if (error) { setEnabled(false); return; }
      setEnabled((data?.status ?? "").toLowerCase() === "enabled");
    })();
    return () => { alive = false; };
  }, [featureKey]);
  return enabled;
}

function formatTripDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("de-CH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const ARCHIVE_AFTER_DAYS = 14;

export default function Trips() {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [pendingTrips, setPendingTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  const canCreateTrips = useFeatureAccess("create_trips");
  const waitlistEnabled = useGlobalFlagInline("mvp_waitlist_enabled");

  const gateCreate = useMemo(() => {
    if (canCreateTrips === null || waitlistEnabled === null) return false;
    return canCreateTrips === false && waitlistEnabled === true;
  }, [canCreateTrips, waitlistEnabled]);

  const fetchTrips = async () => {
    if (!user) return;

    const { data: pendingRequests } = await supabase
      .from("trip_participant_requests")
      .select("trip_id, trips(id, name, description, start_date, end_date, cover_image_url, creator_id)")
      .eq("user_id", user.id)
      .eq("status", "pending");

    setPendingTrips(
      ((pendingRequests ?? []) as unknown as TripRequest[]).flatMap(
        (r) => (Array.isArray(r.trips) ? r.trips : r.trips ? [r.trips] : [])
      )
    );

    const { data: participantTrips } = await supabase
      .from("trip_participants")
      .select("trip_id, is_co_organizer, is_blocked, trips(id, name, description, start_date, end_date, cover_image_url, creator_id, quiz_enabled)")
      .eq("user_id", user.id)
      .or("is_blocked.is.false,is_blocked.is.null");

    const tripsFromParticipants = ((participantTrips ?? []) as TripJoinEntry[])
      .flatMap((e) => e.trips)
      .filter((t): t is Trip => t !== null);

    const { data: ownTrips } = await supabase
      .from("trips")
      .select("id, name, description, start_date, end_date, cover_image_url, creator_id, quiz_enabled")
      .eq("creator_id", user.id);

    const map = new Map<string, Trip>();
    [...tripsFromParticipants, ...(ownTrips ?? [])].forEach((t) => map.set(t.id, t));

    setTrips(
      Array.from(map.values()).sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      )
    );
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      await fetchTrips();
      setLoading(false);
    })();
  }, [user]);

  const handleWaitlistSubmit = async (msg: string) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return;
    await supabase.from("waitlist_requests").insert({ user_id: auth.user.id, message: msg });
    setShowWaitlistModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <span className="text-slate-500 text-sm animate-pulse">Lade Reisen...</span>
      </div>
    );
  }

  const now = new Date();
  const activeTrips = trips.filter(
    (t) => new Date(t.end_date).getTime() + ARCHIVE_AFTER_DAYS * 86400000 >= now.getTime()
  );
  const archivedTrips = trips.filter(
    (t) => new Date(t.end_date).getTime() + ARCHIVE_AFTER_DAYS * 86400000 < now.getTime()
  );
  const isEmpty = activeTrips.length === 0 && pendingTrips.length === 0 && archivedTrips.length === 0;

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-24 px-4 sm:px-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Meine Reisen</h1>
          <button
            onClick={() => gateCreate ? setShowWaitlistModal(true) : setShowCreateModal(true)}
            className="bg-sky-500 hover:bg-sky-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            + Neue Reise
          </button>
        </div>

        {/* Empty state */}
        {isEmpty && (
          <div className="mt-6 bg-gradient-to-br from-sky-900/30 to-slate-800/50 border border-[#2a3347] rounded-2xl p-8 text-center space-y-4">
            <div className="text-5xl mb-2">🌍</div>
            <h2 className="text-xl font-bold text-slate-100">Noch keine Reisen</h2>
            <p className="text-slate-400 text-sm max-w-xs mx-auto">
              Erstelle deine erste Reise oder warte auf eine Einladung.
            </p>
            {gateCreate && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400 p-3 text-sm">
                Du bist aktuell auf der Warteliste.
              </div>
            )}
            <button
              onClick={() => gateCreate ? setShowWaitlistModal(true) : setShowCreateModal(true)}
              className="bg-sky-500 hover:bg-sky-600 text-white py-2.5 px-6 rounded-xl font-medium transition-colors mt-2"
            >
              {gateCreate ? "Warum möchtest du mitmachen?" : "+ Neue Reise erstellen"}
            </button>
          </div>
        )}

        {/* Aktive Reisen */}
        {activeTrips.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-4">
              🧳 Aktuelle Reisen
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {activeTrips.map((trip) => (
                <div
                  key={trip.id}
                  onClick={() => navigate(`/trip/${trip.id}`)}
                  className="bg-[#161b27] rounded-2xl overflow-hidden border border-[#2a3347] hover:border-sky-500/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                >
                  {trip.cover_image_url ? (
                    <img src={trip.cover_image_url} alt={trip.name} className="w-full h-44 object-cover" />
                  ) : (
                    <div className="w-full h-44 bg-gradient-to-br from-sky-900/30 to-slate-800 flex items-center justify-center">
                      <span className="text-4xl">🌍</span>
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-400">
                      Aktiv
                    </span>
                    <h2 className="text-base font-semibold text-slate-100 mt-2">{trip.name}</h2>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatTripDate(trip.start_date)} – {formatTripDate(trip.end_date)}
                    </p>
                    {trip.description && (
                      <p className="text-sm text-slate-400 mt-2 line-clamp-2">{trip.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Angefragte Reisen */}
        {pendingTrips.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-4">
              ⏳ Angefragte Reisen
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {pendingTrips.map((trip) => (
                <div
                  key={trip.id}
                  className="bg-[#161b27] rounded-2xl overflow-hidden border border-[#2a3347] opacity-70"
                >
                  {trip.cover_image_url ? (
                    <img src={trip.cover_image_url} alt={trip.name} className="w-full h-44 object-cover grayscale" />
                  ) : (
                    <div className="w-full h-44 bg-slate-800 flex items-center justify-center">
                      <span className="text-4xl grayscale">🌍</span>
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                      🕓 Warten auf Freigabe
                    </span>
                    <h2 className="text-base font-semibold text-slate-300 mt-2">{trip.name}</h2>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatTripDate(trip.start_date)} – {formatTripDate(trip.end_date)}
                    </p>
                    <button
                      onClick={async () => {
                        await supabase
                          .from("trip_participant_requests")
                          .delete()
                          .eq("trip_id", trip.id)
                          .eq("user_id", user!.id);
                        await fetchTrips();
                      }}
                      className="mt-3 text-xs text-sky-400 hover:text-sky-300 underline"
                    >
                      Anfrage zurückziehen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Archivierte Reisen */}
        {archivedTrips.length > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setShowArchive(!showArchive)}
              className="text-xs text-slate-500 hover:text-slate-300 underline mb-4 transition-colors"
            >
              {showArchive
                ? "Vergangene Reisen ausblenden"
                : `📦 ${archivedTrips.length} vergangene Reise${archivedTrips.length !== 1 ? "n" : ""} anzeigen`}
            </button>
            {showArchive && (
              <div className="grid gap-4 sm:grid-cols-2">
                {archivedTrips.map((trip) => (
                  <div
                    key={trip.id}
                    onClick={() => navigate(`/trip/${trip.id}`)}
                    className="bg-[#161b27] rounded-2xl overflow-hidden border border-[#2a3347] hover:border-slate-500/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer opacity-60 hover:opacity-80"
                  >
                    {trip.cover_image_url ? (
                      <img src={trip.cover_image_url} alt={trip.name} className="w-full h-36 object-cover grayscale" />
                    ) : (
                      <div className="w-full h-36 bg-slate-800 flex items-center justify-center">
                        <span className="text-3xl grayscale">🌍</span>
                      </div>
                    )}
                    <div className="p-4">
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-500">
                        Archiviert
                      </span>
                      <h2 className="text-base font-semibold text-slate-400 mt-2">{trip.name}</h2>
                      <p className="text-xs text-slate-600 mt-1">
                        {formatTripDate(trip.start_date)} – {formatTripDate(trip.end_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {showCreateModal && (
        <CreateTripModal
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => { await fetchTrips(); }}
        />
      )}

      <WaitlistModalInline
        open={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        onSubmit={handleWaitlistSubmit}
      />
    </div>
  );
}

function WaitlistModalInline({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (message: string) => Promise<void> | void;
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const disabled = message.trim().length === 0 || message.length > 500;

  const submit = async () => {
    setSending(true);
    try {
      await onSubmit(message.trim());
      setMessage("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-lg rounded-2xl p-6 shadow-xl">
        <h2 className="text-lg font-semibold mb-2 text-slate-100">Warteliste für Svevana</h2>
        <p className="text-sm text-slate-400 mb-3">
          Kurz: Warum möchtest du Svevana testen? (max. 500 Zeichen)
        </p>
        <textarea
          className="w-full border border-[#2a3347] rounded-lg p-3 min-h-[120px] outline-none bg-[#0d1117] text-slate-100 placeholder:text-slate-600 focus:border-sky-400"
          value={message}
          maxLength={500}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Dein Kommentar…"
        />
        <div className="flex justify-between items-center text-xs text-slate-500 mt-2">
          <span>{message.length}/500</span>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button
            className="px-3 py-2 rounded-lg border border-[#2a3347] text-slate-300 bg-[#1e2639] hover:bg-[#2a3347]"
            onClick={onClose}
            disabled={sending}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
            disabled={disabled || sending}
            onClick={submit}
          >
            {sending ? "Sende…" : "Senden"}
          </button>
        </div>
      </div>
    </div>
  );
}
